import { FormGroup, InputGroup, TextArea, Checkbox } from "@blueprintjs/core"
import CustomDateInput from "components/Atoms/CustomDateInput"
import CustomDialog from "components/Atoms/CustomDialog"
import DynamicSelector from "components/Molecules/DynamicSelector"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import { useShipmentPlanBySoNoQuery } from "generated/graphql"
import React, { useEffect, useState } from "react"
import { VARIETIES, SKUS, EXPORT_CUSTOMER, PORT, COLOR_GROUP, PRIMARY_PACK_MATERIAL, SECONDARY_PACK_MATERIAL } from "graphql/selector.queries.gql"
import StaticSelector from "components/Atoms/StaticSelector"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { CREATE_UPDATE_SALES_ORDER } from "graphql/production/salesOrder.mutation.gql"



const SalesOrderEditUpdate = ({
    isOpen,
    onClose,
    soId
}: {
    isOpen: boolean
    onClose: () => void
    soId: string
}) => {
    const [formData, setFormData] = useState({
        id: "",
        weeklySrNo: "",
        soNo: "",
        labels: "",
        gtin: "",
        eta: null,
        etd: null,
        varietyType: "MIX",
        saleType: "",
        exportCustomer: null,
        port: null,
        skuType: null,
        colourGroup: null,
        varieties: [],
        primaryPackMaterial: null,
        secondaryPackMaterial: null,
        skuFilled: 0,
        isEuro: false,
        regularBoxCount: 0,
        euroBoxCount: 0,
    })

    const [isAllDisable, setIsAllDisable] = useState(false)

    const {
        data: sData,
        refetch,
        loading,
        error,
    } = useShipmentPlanBySoNoQuery({
        variables: {
            soNo: soId,
        },
        fetchPolicy: "network-only",
        skip: !soId || !isOpen,
    })

    const salesOrder = sData?.shipmentPlanById




    // Pre-populate form when data loads
    useEffect(() => {
        if (salesOrder) {
            // Parse metadata
            let metadata = { is_euro: false, regular_pallet_box_count: 0, euro_pallet_box_count: 0 }
            try {
                if (salesOrder.metadata) {
                    metadata = JSON.parse(salesOrder.metadata)
                }
            } catch (e) {
                console.error("Error parsing metadata:", e)
            }

            setFormData({
                id: salesOrder.id,
                weeklySrNo: salesOrder.weeklySrNo || "",
                soNo: salesOrder?.soNo || "",
                labels: salesOrder?.labels || "",
                gtin: salesOrder?.gtin || "",
                eta: salesOrder?.eta || null,
                etd: salesOrder?.etd || null,
                varietyType: salesOrder?.varietyType || "SINGLE",
                saleType: salesOrder.saleType || "",
                exportCustomer: salesOrder.exportCustomer || null,
                port: salesOrder.port || null,
                skuType: salesOrder.skuType || null,
                colourGroup: salesOrder.colourGroup || null,
                varieties: salesOrder.varieties || [],
                primaryPackMaterial: salesOrder.primaryPackMaterial || null,
                secondaryPackMaterial: salesOrder.secondaryPackMaterial || null,
                skuFilled: salesOrder.skuFilled || 0,
                isEuro: metadata.is_euro || false,
                regularBoxCount: metadata.regular_pallet_box_count || 0,
                euroBoxCount: metadata.euro_pallet_box_count || 0,
            })

            setIsAllDisable(salesOrder.approvedtradeprofileSet?.length > 0)
        }
    }, [salesOrder, sData])

    // Validation state
    const [errors, setErrors] = useState<Record<string, string>>({})

    // Validation function
    const validateForm = () => {
        const newErrors: Record<string, string> = {}

        if (!formData.soNo) newErrors.soNo = "SO Number is required"
        if (!formData.weeklySrNo) newErrors.weeklySrNo = "Weekly Sr. No. is required"
        if (!formData.exportCustomer) newErrors.exportCustomer = "Export Customer is required"
        if (!formData.varietyType) newErrors.varietyType = "Variety Type is required"
        if (!formData.skuType) newErrors.skuType = "SKU Type is required"
        if (!formData.port) newErrors.port = "Port is required"
        if (!formData.colourGroup) newErrors.colourGroup = "Color Group is required"
        if (!formData.varieties || formData.varieties.length === 0) newErrors.varieties = "At least one variety is required"
        if (!formData.primaryPackMaterial) newErrors.primaryPackMaterial = "Primary Pack Material is required"
        if (!formData.secondaryPackMaterial) newErrors.secondaryPackMaterial = "Secondary Pack Material is required"
        if (!formData.skuFilled || formData.skuFilled <= 0) newErrors.skuFilled = "SKU Filled must be greater than 0"
        if (!formData.regularBoxCount || formData.regularBoxCount <= 0) newErrors.regularBoxCount = "Regular Box Count must be greater than 0"
        if (formData.isEuro && (!formData.euroBoxCount || formData.euroBoxCount <= 0)) {
            newErrors.euroBoxCount = "Euro Box Count must be greater than 0"
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }


    if (loading) {
        return (
            <CustomDialog
                width="70%"
                icon="document-share"
                title="Edit Sales Order"
                isOpen={isOpen}
                onClose={onClose}
            >
                <div className="p-8 text-center">Loading...</div>
            </CustomDialog>
        )
    }

    if (error) {
        return (
            <CustomDialog
                width="70%"
                icon="document-share"
                title="Edit Sales Order"
                isOpen={isOpen}
                onClose={onClose}
            >
                <div className="p-8 text-center text-red-600">
                    Error loading sales order: {error.message}
                </div>
            </CustomDialog>
        )
    }

    return (
        <CustomDialog
            width="80%"
            fixedHeader
            icon="document-share"
            title={`Edit Sales Order - ${formData.soNo}`}
            isOpen={isOpen}
            onClose={onClose}
            isCloseButtonShown={false}
            footerRightButton={
                <div className="flex items-center justify-end w-full">
                    <ButtonMutation
                        mutation={CREATE_UPDATE_SALES_ORDER}
                        disabled={isAllDisable}

                        validate={() => {
                            const isValid = validateForm()
                            if (!isValid) {
                                console.error("Validation failed. Please fill all required fields.")
                            }
                            return isValid
                        }}
                        variables={{
                            input: {
                                id: formData.id,
                                soNo: formData.soNo,
                                weeklySrNo: formData.weeklySrNo,
                                colourGroupId: formData.colourGroup?.id,
                                exportCustomerId: formData.exportCustomer?.id,
                                portId: formData.port?.id,
                                primaryPackMaterialId: formData.primaryPackMaterial?.id,
                                secondaryPackMaterialId: formData.secondaryPackMaterial?.id,
                                skuTypeId: formData.skuType?.id,
                                varieties: formData.varieties.map((variety) => variety.id),
                                saleType: formData.saleType,
                                varietyType: formData.varietyType,
                                metadata: {
                                    is_euro: formData.isEuro,
                                    regular_pallet_box_count: formData.regularBoxCount,
                                    euro_pallet_box_count: formData.euroBoxCount,
                                },
                                eta: formData.eta,
                                etd: formData.etd,
                                labels: formData.labels,
                                gtin: formData.gtin,
                                skuFilled: formData.skuFilled,

                            }
                        }}
                        onCompleted={() => {
                            onClose()
                        }}
                        onError={(error) => {
                            console.error("Error updating sales order:", error)
                        }}
                        text="Update"
                        dark={false}
                        intent="success"
                    />
                </div>
            }
        >
            <div className="bg-white rounded-lg p-6">
                {isAllDisable && (
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg flex items-center gap-2">
                        <span className="text-yellow-700 font-semibold">⚠️ MH Code Assigned</span>
                        <span className="text-yellow-600 text-sm">This sales order cannot be edited because MH codes have been assigned.</span>
                    </div>
                )}
                <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-3">
                        <FormGroup
                            label={<>Sales Order no. (SO) <span className="text-red-600">*</span></>}
                            helperText={errors.soNo}
                            intent={errors.soNo ? "danger" : "none"}
                        >
                            <InputGroup
                                disabled={isAllDisable}
                                value={formData.soNo}
                                onChange={(e) => {
                                    setFormData({ ...formData, soNo: e.target.value })
                                    if (errors.soNo) setErrors({ ...errors, soNo: "" })
                                }}
                                placeholder="Enter SO number"
                                intent={errors.soNo ? "danger" : "none"}
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup
                            label={<>Weekly Sr. No. <span className="text-red-600">*</span></>}
                            helperText={errors.weeklySrNo}
                            intent={errors.weeklySrNo ? "danger" : "none"}
                        >
                            <InputGroup
                                disabled={isAllDisable}
                                value={formData.weeklySrNo}
                                onChange={(e) => {
                                    setFormData({ ...formData, weeklySrNo: e.target.value })
                                    if (errors.weeklySrNo) setErrors({ ...errors, weeklySrNo: "" })
                                }}
                                placeholder="Enter weekly serial number"
                                intent={errors.weeklySrNo ? "danger" : "none"}
                            />
                        </FormGroup>
                    </div>
                    <div className="col-span-3">
                        <FormGroup label={<>Export Customer <span className="text-red-600">*</span></>}>
                            <DynamicSelector
                                disabled={isAllDisable}
                                query={EXPORT_CUSTOMER}
                                value={formData.exportCustomer}
                                onChange={(_, value) => setFormData({ ...formData, exportCustomer: value })}
                                placeholder="Select customer"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>SKU Type <span className="text-red-600">*</span></>}>
                            <DynamicSelector
                                disabled={isAllDisable}
                                query={SKUS}
                                value={formData.skuType}
                                onChange={(_, value) => setFormData({ ...formData, skuType: value })}
                                placeholder="Select SKU type"
                            />
                        </FormGroup>
                    </div>



                    <div className="col-span-3">
                        <FormGroup label={<>Port <span className="text-red-600">*</span></>}>
                            <DynamicSelector
                                disabled={isAllDisable}
                                query={PORT}
                                value={formData.port}
                                onChange={(_, value) => setFormData({ ...formData, port: value })}
                                placeholder="Select port"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label="ETD (Estimated Time of Departure)">
                            <CustomDateInput
                                disabled={isAllDisable}
                                path="etd"
                                value={formData.etd}
                                onChange={(path, date) => {
                                    setFormData({ ...formData, etd: date })
                                }}
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label="ETA (Estimated Time of Arrival)">
                            <CustomDateInput
                                disabled={isAllDisable}
                                path="eta"
                                value={formData.eta}
                                onChange={(path, date) => {
                                    setFormData({ ...formData, eta: date })
                                }}
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>Color Group <span className="text-red-600">*</span></>}>
                            <DynamicSelector
                                disabled={isAllDisable}
                                query={COLOR_GROUP}
                                value={formData.colourGroup}
                                onChange={(_, value) => setFormData({ ...formData, colourGroup: value })}
                                placeholder="Select color group"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>Variety Type <span className="text-red-600">*</span></>}>
                            <StaticSelector
                                disabled={isAllDisable}
                                items={[
                                    { name: "MIX", id: "MIX" },
                                    { name: "SINGLE", id: "SINGLE" },
                                ]}
                                value={
                                    formData.varietyType
                                        ? { id: formData.varietyType, name: formData.varietyType }
                                        : null
                                }
                                onChange={(_, value) => {
                                    const newType = value?.id || ""
                                    if (newType === "SINGLE" && formData.varieties.length > 1) {
                                        setFormData({
                                            ...formData,
                                            varietyType: newType,
                                            varieties: [formData.varieties[0]]
                                        })
                                    } else {
                                        setFormData({ ...formData, varietyType: newType })
                                    }
                                }}
                            />
                        </FormGroup>
                    </div>





                    <div className="col-span-3">
                        <FormGroup
                            label={<>Varieties <span className="text-red-600">*</span></>}
                            helperText={
                                formData.varietyType === "SINGLE"
                                    ? formData.varieties?.length > 0
                                        ? `Selected: ${formData.varieties[0]?.name || ""}`
                                        : "Select one variety"
                                    : formData.varieties?.length > 0
                                        ? `${formData.varieties.length} selected: ${formData.varieties.map(v => v.name).join(", ")}`
                                        : "Select multiple varieties"
                            }
                        >
                            {formData.varietyType === "SINGLE" ? (
                                <DynamicSelector
                                    disabled={isAllDisable}
                                    query={VARIETIES}
                                    variables={{ includeCommodity: false }}
                                    value={formData.varieties && formData.varieties.length > 0 ? formData.varieties[0] : null}
                                    onChange={(_, value) => {
                                        setFormData({ ...formData, varieties: value ? [value] : [] })
                                    }}
                                    placeholder="Select variety"
                                />
                            ) : (
                                <DynamicMultiSelector
                                    disabled={isAllDisable}
                                    query={VARIETIES}
                                    variables={{ includeCommodity: false }}
                                    value={formData.varieties || []}
                                    onChange={(_, value) => {
                                        setFormData({ ...formData, varieties: value || [] })
                                    }}
                                    placeholder="Select varieties"
                                />
                            )}
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>Primary Pack Material <span className="text-red-600">*</span></>}>
                            <DynamicSelector
                                disabled={isAllDisable}
                                query={PRIMARY_PACK_MATERIAL}
                                value={formData.primaryPackMaterial}
                                onChange={(_, value) => setFormData({ ...formData, primaryPackMaterial: value })}
                                placeholder="Select primary packing"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>Secondary Pack Material <span className="text-red-600">*</span></>}>
                            <DynamicSelector
                                disabled={isAllDisable}
                                query={SECONDARY_PACK_MATERIAL}
                                value={formData.secondaryPackMaterial}
                                onChange={(_, value) => setFormData({ ...formData, secondaryPackMaterial: value })}
                                placeholder="Select secondary packing"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label="Labels">
                            <InputGroup
                                disabled={isAllDisable}
                                value={formData.labels}
                                onChange={(e) => setFormData({ ...formData, labels: e.target.value })}
                                placeholder="Enter labels"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label="GTIN">
                            <InputGroup
                                disabled={isAllDisable}
                                value={formData.gtin}
                                onChange={(e) => setFormData({ ...formData, gtin: e.target.value })}
                                placeholder="Enter GTIN"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label="Sale Type">
                            <InputGroup
                                disabled={isAllDisable}
                                value={formData.saleType}
                                onChange={(e) => setFormData({ ...formData, saleType: e.target.value })}
                                placeholder="Enter sale type"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>SKU Filled <span className="text-red-600">*</span></>}>
                            <InputGroup
                                disabled={isAllDisable}
                                type="number"
                                min="0"
                                step="1"
                                value={formData.skuFilled.toString()}
                                onChange={(e) => setFormData({ ...formData, skuFilled: parseFloat(e.target.value) || 0 })}
                                placeholder="Enter SKU filled"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>Regular Box Count <span className="text-red-600">*</span></>}>
                            <InputGroup
                                disabled={isAllDisable}
                                type="number"
                                min="0"
                                step="1"
                                value={formData.regularBoxCount.toString()}
                                onChange={(e) => setFormData({ ...formData, regularBoxCount: parseInt(e.target.value) || 0 })}
                                placeholder="Enter regular box count"
                            />
                        </FormGroup>
                    </div>

                    <div className="col-span-3">
                        <FormGroup label={<>Is Euro Pallet <span className="text-red-600">*</span></>}>
                            <Checkbox
                                disabled={isAllDisable}
                                checked={formData.isEuro}
                                onChange={(e) => setFormData({ ...formData, isEuro: e.currentTarget.checked })}
                                label={formData.isEuro ? "Yes" : "No"}
                                large
                            />
                        </FormGroup>
                    </div>

                    {formData.isEuro && (
                        <div className="col-span-3">
                            <FormGroup label={<>Euro Box Count <span className="text-red-600">*</span></>}>
                                <InputGroup
                                    disabled={isAllDisable}
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={formData.euroBoxCount.toString()}
                                    onChange={(e) => setFormData({ ...formData, euroBoxCount: parseInt(e.target.value) || 0 })}
                                    placeholder="Enter euro box count"
                                />
                            </FormGroup>
                        </div>
                    )}
                </div>
            </div>
        </CustomDialog>
    )
}

export default SalesOrderEditUpdate